<template>
  <div :class="getWrapperClass()">
    <label v-if="label" class="input-label" :class="{ regular: regularLabel }">
      {{ label }}
    </label>
    <div class="position-relative">
      <textarea
        :id="id"
        :rows="rows"
        class="form-control"
        :value="value"
        :placeholder="placeholder"
        :maxlength="maxlength"
        :disabled="disabled"
        :class="{
          'was-Invalidated': error,
          'has-ai-icon': showAiIcon,
          'ai-field-highlighted': highlighted,
        }"
        @input="$emit('input', $event.target.value)"
      />
      <span v-if="showCharCount" class="char-count" :class="getCountClasses()">
        <span v-if="maxlength > 0">{{ currentLength }}</span>
        <span v-else>0</span>
        de {{ maxlength }}
      </span>
      <!--
        US 156915 - slot opcional para el ícono del Asistente de IA (esquina del campo). El div padre ".position-relative" es lo que permite que
        el botón (position: absolute) del slot quede anclado dentro del recuadro del
        textarea, no fuera de él.

        Renderizado controlado por la prop showAiIcon (ver más abajo) -- así el "encendido"
        del asistente es una sola propiedad explícita que decide el consumidor del
        componente (ej. el FE de Académico, según su parámetro de sistema
        'EnableAiAssistantPei'), en vez de que cada pantalla tenga que envolver el slot en
        su propio v-if para lograr lo mismo. Si showAiIcon es false (valor por defecto), no
        se renderiza nada -- el resto de las pantallas que usan CustomTextArea sin esta prop
        quedan exactamente igual que antes.
      -->
      <slot v-if="showAiIcon" name="ai-icon" />
    </div>
    <Error v-if="error" variant="no-border" :message="customErrorMsg" />
  </div>
</template>

<script>
import Error from '@/components/common/Error';

export default {
  name: 'CustomTextArea',
  meta: {
    description: 'Componente para entrada de texto multilínea',
    slots: {
      default: {
        type: 'component',
        valid: [],
      },
    },
  },
  components: {
    Error,
  },
  data() {
    return {
      currentValue: '',
    };
  },
  mounted() {
    this.currentValue = this.value;
  },
  computed: {
    currentLength() {
      const text = this.value ?? '';
      return text.length;
    },
  },
  props: {
    id: {
      type: String,
      default: '',
      meta: {
        description: 'Id del Componente CustomTextArea',
        example: 'idCustomTextArea',
      },
    },
    label: {
      type: String,
      default: '',
      meta: {
        description: 'Control para mostrar texto',
        example: 'Texto requerido',
      },
    },
    classes: {
      type: Array,
      default: () => new Array(),
      meta: {
        description: 'Propiedad para almacenar caracteres',
        example: '4, 8, 15',
      },
    },
    countClasses: {
      type: Array,
      default: () => new Array(),
      meta: {
        description: 'Conteo de caracteres',
        example: '16, 23, 42',
      },
    },
    value: {
      type: String,
      default: '',
      meta: {
        description: 'Parámetro de entrada en el textarea',
        example: 'Texto requerido',
      },
    },
    maxlength: {
      type: Number,
      default: 0,
      required: false,
      meta: {
        description: 'Logitud máxima requerida',
        example: 'Texto requerido',
      },
    },
    disabled: {
      type: Boolean,
      required: false,
      meta: {
        description: 'Estado Disabled',
        example: 'true',
      },
    },
    error: {
      type: String,
      default: '',
      meta: {
        description: 'Estado Error',
        example: 'Texto requerido',
      },
    },
    customErrorMsg: {
      type: String,
      default: function() {
        return this.error;
      },
      meta: {
        description: 'Mensaje de error encontrado',
        example: 'Texto requerido',
      },
    },
    placeholder: {
      type: String,
      default: '',
      meta: {
        description: 'Texto de referencia en el textarea',
        example: 'Texto requerido',
      },
    },
    invalid: {
      type: Boolean,
      default: false,
      meta: {
        description: 'Propiedad para validar',
        example: 'true',
      },
    },
    rows: {
      type: Number,
      default: 0,
      required: false,
      meta: {
        description: 'Especifica el número de líneas de texto visibles',
        example: '100',
      },
    },
    showCharCount: {
      type: Boolean,
      default: true,
    },
    regularLabel: {
      type: Boolean,
      default: () => false,
      meta: {
        description: 'Control para mostrar texto en regular',
        example: 'true',
      },
    },
    // US 156915 - controla si se renderiza el slot "ai-icon" (el botón del Asistente
    // Virtual, esquina del campo). El consumidor (ej. FormTextArea, y a través de este,
    // la pantalla del FE de Académico) decide el valor -- normalmente según su propio
    // parámetro de sistema para activar/desactivar el asistente de IA.
    showAiIcon: {
      type: Boolean,
      default: false,
      meta: {
        description:
          'Muestra el ícono del Asistente Virtual en la esquina del campo',
        example: 'true',
      },
    },
    // US 156915 -- resalta (border + fondo violeta, ver .ai-field-highlighted más abajo) la
    // caja del <textarea> mismo mientras el Asistente de IA está redactando/mostrando una
    // sugerencia para ESTE campo. Deliberadamente en la caja real, no en un wrapper externo
    // que también incluya el label -- así el resaltado se ve igual en cualquier pantalla que
    // use CustomTextArea (o FormTextArea), sin que cada una tenga que reproducir el estilo.
    // Por defecto false -- el resto de las pantallas que no pasan esta prop quedan igual.
    highlighted: {
      type: Boolean,
      default: false,
      meta: {
        description:
          'Resalta el textarea con border y fondo violeta (uso del Asistente de IA)',
        example: 'true',
      },
    },
  },
  methods: {
    getWrapperClass: function() {
      return this.getClasses(this.classes);
    },
    getCountClasses: function() {
      return this.getClasses(this.countClasses);
    },
    getClasses: function(values) {
      return values.map((element) => {
        return { [element]: true };
      });
    },
    updateValue: function(value) {
      this.$emit('input', value);
    },
  },
};
</script>
<style scoped>
textarea {
  background: #ffffff 0% 0% no-repeat padding-box;
  min-height: 100px;
}

.input-label {
  height: 19px;
  text-align: left;
  font: Bold 16px/21px Lato;
  letter-spacing: 0;
  color: gray;
  line-height: 19px;
}

span.invalid:not(input) {
  color: #e85c5c;
}

.regular {
  font-weight: 400;
}

textarea::placeholder {
  text-align: left;
  font: Regular 16px/20px Lato;
  letter-spacing: 0;
  color: #828d99;
  opacity: 1;
}

textarea:disabled {
  background: #f8f8f8;
}

.form-control {
  background: #ffffff 0% 0% no-repeat padding-box;
  border: 1px solid #c5cdcf;
  opacity: 1;
}

/*
  US 156915 - cuando el ícono del Asistente de IA está activo (showAiIcon), el ícono queda
  anclado con position:absolute en la esquina superior derecha del recuadro (ver
  AiSuggestionIcon.vue: top:10px; right:14px; 30px de alto). Un <textarea> no puede "recortar"
  el texto alrededor de un elemento flotante como un párrafo de HTML normal -- si el texto
  del usuario alcanza esa esquina (algo que depende del contenido, no es reproducible siempre),
  el ícono queda visualmente encima del texto, tapándolo.

  En vez de reaccionar después del hecho, se reserva el espacio de antemano (mismo enfoque que
  el "reserve space" del modal del asistente): se agrega padding-top al propio textarea para
  que NINGUNA línea de texto pueda aparecer en la franja donde vive el ícono, sin importar
  cuánto texto escriba el usuario. 10px (top del ícono) + 30px (alto del ícono) + ~8px de
  margen = 48px.

  IMPORTANTE: se fija box-sizing:border-box SIN subir min-height. La idea es que el tamaño
  TOTAL del campo se mantenga exactamente igual al de siempre (min-height: 100px, el mismo
  valor que ya tenía el resto de la app) -- no queremos una caja más alta/con más espacio en
  blanco quede de más cuando el comentario es corto. Con border-box, el padding-top se descuenta
  DEL espacio de escritura existente (en vez de sumarse aparte y agrandar la caja), es decir: se
  cede un poco de área visible para escribir sin scroll (100px - 48px ≈ 52px libres arriba de
  todas formas se puede escribir de sobra, ya que el campo permite scroll hasta 10000
  caracteres) a cambio de que el ícono nunca tape texto. Si en algún campo puntual 52px de alto
  visible resulta muy poco, es preferible subir el rows/min-height de ESE campo puntual en
  Tab3.vue, no inflar aquí el tamaño de TODOS los campos con ícono de IA.
*/
.form-control.has-ai-icon {
  box-sizing: border-box;
  padding-top: 48px;
}

/* US 156915 -- border + fondo violeta directamente en la caja del textarea (no en un
   wrapper externo) mientras el Asistente de IA está activo para este campo, ver prop
   `highlighted` más arriba. Mismo violeta que el resto del asistente (ver
   AiAssistantModal.vue). */
.form-control.ai-field-highlighted {
  border: 1.5px solid #ba65ff;
  border-radius: 10px;
  background-color: #f8f5ff;
}

.form-control-icon-invalid {
  color: #e85c5c;
  position: absolute;
  top: 9px;
  right: 10px;
  font-size: 19px;
}

.char-count {
  margin-top: 2px;
  font-weight: normal;
  font-size: 15px;
}

.was-Invalidated,
.was-Invalidated:focus {
  background-color: #f7d7da;
  border: 1px solid #ff0018 !important;
}

.small-counter {
  font: normal normal normal 12px/15px Lato;
  color: #727272;
}
</style>
